import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { GreasingPressureTableComponent } from 'app/modules/cdft/components/greasing-pressure-table/greasing-pressure-table.component';
import { ValveEnum } from 'app/modules/cdft/model';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { CDFTState } from 'app/modules/cdft/state/cdft.state';
import module from 'app/modules/cdft/cdft.module';

@Component({
    selector: 'wellhead-valve',
    templateUrl: './wellhead-valve.component.html',
    standalone: true,
    styleUrls: ['./wellhead-valve.component.scss'],
    imports: [...module, GreasingPressureTableComponent],
})
export class WellheadValveComponent {
    @Input() isViewOnly: boolean;
    formGroup: FormGroup;

    ValveEnum = ValveEnum;
    step = -1;
    valves = [];

    constructor(
        private formBuilder: FormBuilder,
        private state: CDFTState,
        private changeDetectorRef: ChangeDetectorRef
    ) {}

    ngOnInit() {
        this.formGroup = this.onFormGroup();
        this.state.setFormGroup('cdftXmtWhvTest', this.formGroup);
        this.onValve();

        this.formGroup?.valueChanges.subscribe((valveList) => {
            this.state.recalculate();
            this.changeDetectorRef.markForCheck();
        });
    }

    onValve() {
        this.valves = [
            {
                type: ValveEnum.WV,
                title: 'To test the Production Wing Valve (WV)',
                formGroup: this.formGroup.get('wValve'),
            },
            {
                type: ValveEnum.CV,
                title: 'To test the Crown Valve (CV)',
                formGroup: this.formGroup.get('cValve'),
            },
            {
                type: ValveEnum.SSV,
                title: 'To test the Surface Safety Valve (SSV)',
                formGroup: this.formGroup.get('ssValve'),
            },
            {
                type: ValveEnum.LMV,
                title: 'To test the Lower Master Valve (LMV)',
                formGroup: this.formGroup.get('lmValve'),
            },
            {
                type: ValveEnum.KWV,
                title: 'To test the Kill Wing Valve (KWV)',
                formGroup: this.formGroup.get('kwValve'),
            },
            {
                type: ValveEnum.annulusA,
                title: "To test the Production Casing Head Valve (PCV) ('A' annulus)",
                formGroup: this.formGroup.get('annulusA'),
            },
            {
                type: ValveEnum.annulusB,
                title: "To test the Intermediate Casing Head Valve (ICV) ('B' annulus)",
                formGroup: this.formGroup.get('annulusB'),
            },
            {
                type: ValveEnum.annulusC,
                title: "To test the Surface Casing Head Valve (SCV) ('C' annulus)",
                formGroup: this.formGroup.get('annulusC'),
            },
            {
                type: ValveEnum.annulusD,
                title: "To test the Conductor Casing Head Valve (CCV) ('D' annulus)",
                formGroup: this.formGroup.get('annulusD'),
            },
        ];
    }

    onFormGroup() {
        return this.formBuilder.group({
            wValve: this.wellHeadValveFormGroup(),
            cValve: this.formBuilder.group({
                ...this.wellHeadValveFormGroup().controls,
                additionalGateValveTestingRemarks: new FormControl({
                  value: null,
                  disabled: this.isViewOnly,
                }),
              }),
            ssValve: this.wellHeadValveFormGroup(),
            lmValve: this.wellHeadValveFormGroup(),
            kwValve: this.wellHeadValveFormGroup(),
            annulusA: this.wellHeadValveFormGroup(),
            annulusB: this.wellHeadValveFormGroup(),
            annulusC: this.wellHeadValveFormGroup(),
            annulusD: this.wellHeadValveFormGroup(),
        });
    }

    wellHeadValveFormGroup() {
        return this.formBuilder.group({
            id: null,
            mdSceValveTypeId: new FormControl({
                value: null,
                disabled: true,
            }),
            sizeIn: new FormControl({ value: null, disabled: true }),
            equipmentBrand: new FormControl({ value: null, disabled: true }),
            actuatorModel: new FormControl({ value: null, disabled: true }),
            actuatorType: new FormControl({ value: null, disabled: true }),
            isActuated: new FormControl({ value: null, disabled: true }),
            oem: new FormControl({ value: null, disabled: true }),
            ratingPsi: new FormControl({ value: null, disabled: true }),
            openingSec: new FormControl({ value: null, disabled: true }),
            openingCm: new FormControl({ value: null, disabled: true }),
            openingCdftSec: new FormControl({ value: null, disabled: this.isViewOnly }),
            openingCdftCm: new FormControl({ value: null, disabled: this.isViewOnly }),
            closingSec: new FormControl({ value: null, disabled: true }),
            closingCm: new FormControl({ value: null, disabled: true }),
            closingCdftSec: new FormControl({ value: null, disabled: this.isViewOnly }),
            closingCdftCm: new FormControl({ value: null, disabled: this.isViewOnly }),
            valvedTest: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            remarks: new FormControl({ value: null, disabled: true }),
            finding: new FormControl({ value: null, disabled: true }),
            othersFinding: new FormControl({ value: null, disabled: true }),
            fullyOpen: new FormControl({ value: null, disabled: true }),
            fullyClose: new FormControl({ value: null, disabled: true }),
            fullyOpenCdft: new FormControl({ value: null, disabled: this.isViewOnly }),
            fullyCloseCdft: new FormControl({ value: null, disabled: this.isViewOnly }),
            jammed: new FormControl({ value: null, disabled: this.isViewOnly }),
            jammedPosition: new FormControl({ value: null, disabled: this.isViewOnly }),
            hydraulicReturn: new FormControl({ value: null, disabled: this.isViewOnly }),
            greasing: new FormControl({ value: null, disabled: true }),
            greasingType: new FormControl({ value: null, disabled: true }),
            flushing: new FormControl({ value: null, disabled: true }),
            flushingType: new FormControl({ value: null, disabled: true }),
            sealing: new FormControl({ value: null, disabled: true }),
            sealingType: new FormControl({ value: null, disabled: true }),
            actualLeakRate: new FormControl({ value: null, disabled: true }),
            gateValveTesting: new FormControl({ value: null, disabled: this.isViewOnly }),
            cdftXmtWhvGreasing: this.formBuilder.group({
                before: this.formBuilder.group({
                    id: new FormControl({ value: null, disabled: true }),
                    type: new FormControl({ value: 'before', disabled: true }),
                    time0: new FormControl({ value: null, disabled: true }),
                    time1: new FormControl({ value: null, disabled: true }),
                    time2: new FormControl({ value: null, disabled: true }),
                    time3: new FormControl({ value: null, disabled: true }),
                    time4: new FormControl({ value: null, disabled: true }),
                    time5: new FormControl({ value: null, disabled: true }),
                    time6: new FormControl({ value: null, disabled: true }),
                    time7: new FormControl({ value: null, disabled: true }),
                    time8: new FormControl({ value: null, disabled: true }),
                    time9: new FormControl({ value: null, disabled: true }),
                    time10: new FormControl({ value: null, disabled: true }),
                    result: new FormControl({ value: 'UTT', disabled: true }),
                    cdftXmtWhvTestId: new FormControl({
                        value: null,
                        disabled: true,
                    }),
                }),
                after: this.formBuilder.group({
                    id: new FormControl({ value: null, disabled: true }),
                    type: new FormControl({ value: 'after', disabled: true }),
                    time0: new FormControl({ value: null, disabled: true }),
                    time1: new FormControl({ value: null, disabled: true }),
                    time2: new FormControl({ value: null, disabled: true }),
                    time3: new FormControl({ value: null, disabled: true }),
                    time4: new FormControl({ value: null, disabled: true }),
                    time5: new FormControl({ value: null, disabled: true }),
                    time6: new FormControl({ value: null, disabled: true }),
                    time7: new FormControl({ value: null, disabled: true }),
                    time8: new FormControl({ value: null, disabled: true }),
                    time9: new FormControl({ value: null, disabled: true }),
                    time10: new FormControl({ value: null, disabled: true }),
                    result: new FormControl({ value: 'UTT', disabled: true }),
                    cdftXmtWhvTestId: new FormControl({
                        value: null,
                        disabled: true,
                    }),
                }),
            }),
        });
    }

    setStep(index: number) {
        this.step = index;
    }

    nextStep() {
        this.step++;
    }

    prevStep() {
        this.step--;
    }
}
